import { useCallback } from 'react';
import { Clip, ImageMask } from '../types';
import { extractFirstFrame } from '../utils';
import { getClipSourceUrl } from '../utils/clip-helpers';
import { getR2UploadUrl } from '../services/firebase-api';
import { auth, firestore } from '../firebase';

interface UseImageMaskOptions {
    onClipsChange: (updater: (prevClips: Clip[]) => Clip[]) => void;
    exportServiceUrl: string; // Environment variable pointing to the Cloud Run service
}

export function useImageMask({ onClipsChange, exportServiceUrl }: UseImageMaskOptions) {
    const finishGeneration = useCallback((clipId: string, outputMaskUrl: string, prompt: string, cropData?: any) => {
        const newMask: ImageMask = {
            id: `mask-${Date.now()}`,
            url: outputMaskUrl,
            prompt,
            createdAt: Date.now(),
            cropRect: cropData
        };

        onClipsChange(prev => prev.map(c => {
            if (c.id === clipId) {
                const currentMasks = c.imageMasks || [];
                return {
                    ...c,
                    isGeneratingMask: false,
                    imageMasks: [...currentMasks, newMask],
                    activeImageMask: newMask.id // Auto-activate the new mask
                };
            }
            return c;
        }));

        console.log("[ImageMask] Generation complete.", newMask);
    }, [onClipsChange]);

    const resetGeneratingState = useCallback((clipId: string) => {
        onClipsChange(prev => prev.map(c =>
            c.id === clipId ? { ...c, isGeneratingMask: false } : c
        ));
    }, [onClipsChange]);

    const failGeneration = useCallback((clipId: string, message: string) => {
        console.error("[ImageMask] Generation failed:", message);
        resetGeneratingState(clipId);
        alert(`Erreur lors de la generation du masque: ${message}`);
    }, [resetGeneratingState]);

    const pollMaskStatus = useCallback(async (
        clipId: string,
        predictionId: string,
        prompt: string,
        getIdToken: () => Promise<string>,
        cropData?: any
    ) => {
        const endpoint = `${exportServiceUrl}/api/mask-status/${predictionId}`;
        const maxAttempts = 120;
        const pollIntervalMs = 3000;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const idToken = await getIdToken();
                const response = await fetch(endpoint, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${idToken}`
                    }
                });

                const rawBody = await response.text();
                const payload = rawBody ? JSON.parse(rawBody) as {
                    status?: string;
                    outputUrl?: string;
                    error?: string;
                } : {};

                if (!response.ok) {
                    throw new Error(payload.error || rawBody || `Status polling failed (${response.status})`);
                }

                if (payload.status === 'succeeded' && payload.outputUrl) {
                    finishGeneration(clipId, payload.outputUrl, prompt, cropData);
                    return;
                }

                if (payload.status === 'failed' || payload.status === 'canceled') {
                    failGeneration(clipId, payload.error || 'Generation failed');
                    return;
                }
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                console.warn(`[ImageMask] Polling attempt ${attempt + 1} failed:`, message);

                if (attempt === maxAttempts - 1) {
                    failGeneration(clipId, message);
                    return;
                }
            }

            await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
        }

        failGeneration(clipId, 'Timeout while waiting for mask generation');
    }, [exportServiceUrl, failGeneration, finishGeneration]);

    const handleGenerateImageMask = useCallback(async (clip: Clip, prompt: string, customImageBlob?: Blob, cropData?: any) => {
        if (!clip.id || clip.type === 'audio' || clip.type === 'text' || clip.type === 'zoom') {
            console.warn('Image mask only supported for video/image clips.');
            return;
        }

        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                throw new Error("Utilisateur non authentifie. Reconnectez-vous puis reessayez.");
            }

            // 1. Mark clip as generating
            onClipsChange(prev => prev.map(c =>
                c.id === clip.id ? { ...c, isGeneratingMask: true } : c
            ));

            // 2. Extract 1st frame as blob (OR use customImageBlob if provided)
            let frameBlob = customImageBlob;

            if (!frameBlob) {
                const sourceUrl = clip.type === 'video'
                    ? getClipSourceUrl(clip, { preferLocal: true, includeStreamUrl: true, allowStreamingManifest: false })
                    : (clip.localUrl || clip.url);
                if (!sourceUrl) throw new Error("No source URL found for clip.");
                frameBlob = await extractFirstFrame(sourceUrl);
            }

            if (!frameBlob) throw new Error("Failed to extract frame.");

            // 3. Upload frame to R2 for public URL access by Replicate
            // 3. Upload frame to R2 for public URL access by Replicate
            const response = await getR2UploadUrl({ fileName: `mask-inputs/${clip.id}-${Date.now()}.jpg`, contentType: 'image/jpeg' });
            const { uploadUrl, publicUrl: inputImageUrl } = response.data as { uploadUrl: string, publicUrl: string };

            const uploadResp = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'image/jpeg' },
                body: frameBlob
            });

            if (!uploadResp.ok) throw new Error("Failed to upload input frame to R2.");

            // 4. Call export-service backend to trigger Replicate (Nano Banana)
            console.log(`[ImageMask] Calling backend for Nano Banana generation... URL: ${inputImageUrl}`);

            const endpoint = `${exportServiceUrl}/api/generate-mask`;
            const idToken = await currentUser.getIdToken();
            const generationResponse = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    prompt,
                    image_url: inputImageUrl
                })
            });

            if (!generationResponse.ok) {
                const errText = await generationResponse.text();
                throw new Error(`Generation failed: ${errText}`);
            }

            const data = await generationResponse.json();
            const predictionId = data.id;

            if (!predictionId) {
                if (data.url) {
                    // Fallback just in case backend returns URL directly (local testing)
                    finishGeneration(clip.id, data.url, prompt, cropData);
                    return;
                }
                throw new Error("No prediction ID returned from backend.");
            }

            console.log(`[ImageMask] Listening to Firestore for prediction ${predictionId}...`);

            // 5. Listen to Firestore for completion via Webhook
            const unsubscribe = firestore.collection('image-masks').doc(predictionId).onSnapshot(
                (snapshot: any) => {
                    if (!snapshot.exists) return;

                    const maskData = snapshot.data();
                    if (maskData.status === 'succeeded' && maskData.outputUrl) {
                        unsubscribe();
                        finishGeneration(clip.id, maskData.outputUrl, prompt, cropData);
                    } else if (maskData.status === 'failed' || maskData.status === 'canceled') {
                        unsubscribe();
                        console.error("[ImageMask] Error from webhook:", maskData.error);
                        onClipsChange(prev => prev.map(c =>
                            c.id === clip.id ? { ...c, isGeneratingMask: false } : c
                        ));
                        alert(`Erreur lors de la génération du masque: ${maskData.error}`);
                    }
                },
                (snapshotError: Error) => {
                    unsubscribe();
                    console.error("[ImageMask] Firestore listener error:", snapshotError);
                    console.warn("[ImageMask] Falling back to authenticated polling.");
                    void pollMaskStatus(
                        clip.id,
                        predictionId,
                        prompt,
                        () => currentUser.getIdToken(),
                        cropData
                    );
                }
            );

        } catch (err: any) {
            console.error("[ImageMask] Error initiating mask generation:", err);
            // Reset state on failure
            resetGeneratingState(clip.id);
            alert(`Erreur lors de la génération du masque: ${err.message}`);
        }
    }, [exportServiceUrl, finishGeneration, pollMaskStatus, resetGeneratingState]);

    const handleToggleImageMask = useCallback((clip: Clip, maskId: string, active: boolean) => {
        onClipsChange(prev => prev.map(c =>
            c.id === clip.id ? { ...c, activeImageMask: active ? maskId : undefined } : c
        ));
    }, [onClipsChange]);

    const handleDeleteImageMask = useCallback((clip: Clip, maskId: string) => {
        onClipsChange(prev => prev.map(c => {
            if (c.id === clip.id) {
                const newMasks = (c.imageMasks || []).filter(m => m.id !== maskId);
                return {
                    ...c,
                    imageMasks: newMasks.length > 0 ? newMasks : undefined,
                    activeImageMask: c.activeImageMask === maskId ? undefined : c.activeImageMask
                };
            }
            return c;
        }));
    }, [onClipsChange]);

    return {
        handleGenerateImageMask,
        handleToggleImageMask,
        handleDeleteImageMask
    };
}
